import {
    Box,
    Skeleton,
    Switch,
    Typography,
} from "@mui/material";
import React, {Dispatch, useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";

import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import {
    getCompanyFullTimeScheduleLocationSettings,
    setFullTimeScheduleSelectedLocations,
    setFullTimeScheduleToggle
} from "../../../../store/gat-admin-slice";
import AllowedLocations
    from "./allowed-locations";

interface Location {
    id: string;
    name: string;
}

interface LocationSettings {
    locationId: number;
    value: boolean;
}

interface FullTimeSchedulingProps {
    companyId: number;
    newAddOnSettings: any
    setNewAddOnSettings: Dispatch<any>;
    loading: boolean;
}

const FullTimeScheduling: React.FC<FullTimeSchedulingProps> = (
    {
        companyId,
        newAddOnSettings,
        setNewAddOnSettings,
        loading,
    }) => {

    const dispatch = useDispatch();
    const selectedLocations = useSelector(
        (state: any) => state.gatAdminConfiguration.updateCompanyFullTimeScheduleLocationSettings.selectedLocations,
    );
    const toggle = useSelector(
        (state: any) => state.gatAdminConfiguration.updateCompanyFullTimeScheduleLocationSettings.toggle,
    );
    const companyFullTimeLocationSettings = useSelector(
        (state: any) => state.gatAdminConfiguration.getCompanyFullTimeScheduleLocationSettings.locationSettings,
    );
    const {
        getCompanyDataStatus,
        updateCompanySettingsStatus,
        locations
    } = useGatAdminConfigData();

  const activeLocations = useMemo(() => {
    return locations?.filter((location: any) => location.isActive);
  },[locations]);

    useEffect(() => {
        if (toggle) {
            dispatch(getCompanyFullTimeScheduleLocationSettings({ companyId }));
        }
    }, [companyId, dispatch, toggle]);

    useEffect(() => {
        try {
            dispatch(setFullTimeScheduleToggle(newAddOnSettings?.fullTimeSchedulingEnabled));
        } catch (e) {
            // Do nothing
        }
    }, [dispatch, newAddOnSettings?.fullTimeSchedulingEnabled]);

    useEffect(() => {
        try {
            if (companyFullTimeLocationSettings?.length > 0) {
                const enabledLocations = companyFullTimeLocationSettings.filter(
                    (location: LocationSettings) => location.value
                )?.map((location: LocationSettings) => location?.locationId);
                const selectedLocations = activeLocations.filter((location: any) =>
                    enabledLocations?.includes(location.id)
                ).map((location: any) => ({
                    id: location.id,
                    name: location.name,
                }));
                dispatch(setFullTimeScheduleSelectedLocations(selectedLocations));
            } else {
                dispatch(setFullTimeScheduleSelectedLocations([]));
            }
        } catch (e) {
            // Do nothing
        }
    }, [companyFullTimeLocationSettings, dispatch, activeLocations]);

    useEffect(() => {
        try {
            const selectedLocationsId = selectedLocations?.map((location: any) => location.id);
            const companyFullTimeLocationSettingsId = companyFullTimeLocationSettings?.map((location: any) => location.locationId);
            const sortedSelectedLocations = selectedLocationsId?.sort();
            const sortedCompanyFullTimeLocationSettings = companyFullTimeLocationSettingsId?.sort();
            if ( sortedSelectedLocations?.length > 0 &&
                JSON.stringify(sortedSelectedLocations) !== JSON.stringify(sortedCompanyFullTimeLocationSettings)) {
                setNewAddOnSettings((pre: any) => ({
                    ...pre,
                    fullTimeSchedulingEnabled: true,
                }));
            }
        } catch (e) {
            // Do nothing
        }
    }, [companyFullTimeLocationSettings, selectedLocations, setNewAddOnSettings]);


    const handleLocationChange = (
        event: any
    ) => {
        const value = event.target.value;
        const selectedAll = value[value.length - 1] === "all";
        if (selectedAll) {
            if (selectedLocations?.length !== activeLocations.length) {
                dispatch(setFullTimeScheduleSelectedLocations(activeLocations));
            } else {
                dispatch(setFullTimeScheduleSelectedLocations([]));
            }
            return;
        }
        // Filter out the selected locations
        const filteredLocations = activeLocations.filter((location: Location) =>
          value.includes(location.id)
        );
        dispatch(setFullTimeScheduleSelectedLocations(filteredLocations));
    };

    const handleDelete = (
        event: React.MouseEvent,
        locationToDelete: string
    ) => {
        event.stopPropagation();
        dispatch(
          setFullTimeScheduleSelectedLocations(
            selectedLocations.filter(
              (location: { id: string }) => location.id !== locationToDelete
            )
          )
        );
    };

    return (
        <Box
            sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
                marginTop: "32px",
                padding: "16px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "16px",
                            color: "rgba(0, 0, 0, 0.87)",
                        }}
                    >
                        Advanced Scheduling
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "12px",
                            color: "rgba(0, 0, 0, 0.6)",
                        }}
                    >
                        Efficiently allocate available Workers across Jobs and Departments on regular Shifts
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {loading || getCompanyDataStatus === "pending" ? (
                        <Skeleton width={50} height={40} />
                    ) : (
                        <Switch
                            checked={toggle ?? false}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                // dispatch(setFullTimeScheduleToggle(e.target.checked));
                                dispatch(setFullTimeScheduleToggle(isChecked));
                                // if (!e.target.checked) {
                                    setNewAddOnSettings((pre: any) => ({
                                        ...pre,
                                        fullTimeSchedulingEnabled: e.target.checked,
                                    }));
                                // }
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* {toggle ? (
                <Box
                    sx={{
                        marginTop: "20px",
                        marginBottom: "8px",
                        borderRadius: "8px",
                        border: 1,
                        borderWidth: "1px",
                        borderColor: "rgba(0,0,0,0.12)",
                        background: "#FFF",
                        padding: "16px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                        }}
                    >
                        {activeLocations?.length > 0 ?
                            <AllowedLocations
                                loading={
                                    loading
                                }
                                selectedLocations={selectedLocations}
                                handleLocationChange={handleLocationChange}
                                handleDelete={handleDelete}
                                locations={activeLocations}
                                updateStatus={updateCompanySettingsStatus}
                            /> : <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontWeight: 400,
                                fontSize: "12px",
                                color: "rgba(0, 0, 0, 0.6)",
                            }}>
                                Please add a location from the Locations tab to be able to select it for Full Time Scheduling.
                            </Typography>
                        }
                    </Box>
                </Box>
            ) : null} */}
        </Box>
    );
};

export default FullTimeScheduling;
